// Sponsors page content, taken from the existing KUR Sponsors page copy.

import heroImage from "../ASSETS/IMAGES/sponsorsHero.jpg";

export const sponsorsHero = {
  lead: "Leverage our effective and efficient Investigative Site Network (ISN) to access diverse patient populations, with integrated, streamlined processes from start-up to close-out.",
  actions: [
    { label: "Contact the sponsor team", href: "#contact" },
    { label: "Explore our solutions", href: "#solutions" },
  ],
  note: "KUR Research, LLC is part of Advarra's Gene Therapy Ready sites.",
  image: heroImage,
  imageAlt: "Patient stretching in the morning light",
};

export const promises = ["Quick startup", "Diverse populations", "Rapid enrollment", "Quality data"];

export const industry = {
  problem: {
    title: "Current state of the industry",
    lead: "Industry trends continue to demonstrate that slow enrollment is the top issue plaguing clinical research.",
    stats: [
      { value: "80%", text: "of clinical trials experience delayed enrollment" },
      { value: "48%", text: "never achieve the initial enrollment goal" },
    ],
    question:
      "How can pharmaceuticals, biotechs, and CROs mitigate the challenges related to slow enrollment and start-up delays in their clinical trials?",
  },
  answer: {
    title: "Why KUR Research?",
    paragraphs: [
      "With over 11 years of experience, KUR offers innovative solutions to address challenges in clinical research.",
      "Our team delivers high-quality data to our sponsor partners, across all patient age groups, focusing on drug, device, and diagnostic development.",
      "By building a network of various site types and specialties, we are able to meet virtually all sponsor needs through streamlined operations and a focus on quality.",
    ],
  },
};

export const approach = [
  {
    title: "Our Network",
    fact: "KUR-trained site staff",
    text: "Our network is designed to be effective and efficient, and supports expedited patient enrollment. Our investigators attract and retain patients in clinical trials by building patient relationships organically, ensuring patients' engagement, compliance, and retention throughout the trial. Our Investigative Site Network is augmented by KUR-trained and experienced site-level research staff who are focused on patient recruitment, protocol execution, compliance, and data quality.",
  },
  {
    title: "Patient Management",
    fact: "Searchable patient database",
    text: "Our proprietary database of patient information allows us to securely store patient data and efficiently search our database by age, gender, diagnoses, and various inclusion/exclusion criteria. By filtering our database, we can provide sponsors with realistic and accurate enrollment data and tailor our clinical trials to the needs of our patients.",
  },
  {
    title: "Quality Assurance and Data Management",
    fact: "Ready for SDV within 24 hours",
    text: "Our dedicated 24/7 quality assurance team consistently monitors data to ensure data quality and integrity. KUR quality professionals remotely evaluate data collection and regulatory documents, review sponsor feedback from recent monitoring visits, and maintain equipment calibration documentation. We provide Sponsors and CROs access to an eSource system for QA and monitoring activities. The data is ready for source verification and external monitoring by the CRA within 24 hours of each subject visit.",
  },
  {
    title: "Central Clinical Trials Management System",
    fact: "95% digital",
    text: "We are 95% digital and all clinical trial data is documented through our web-based Clinical Trial Management System (CTMS) to ensure quality, accuracy, and timeliness. With the rising need for remote capabilities within the research industry, our CTMS allows for seamless remote monitoring and real-time quality assurance auditing. By utilizing a CTMS, we can accurately track budgets, costs, and invoices to ensure an accurate financial outcome for each trial.",
  },
  {
    title: "Central Marketing",
    fact: "IRB-approved, run centrally",
    text: "KUR Research works closely with sponsors to ensure enrollment and retention goals are maintained throughout the duration of the study. One tool we utilize to combat enrollment hurdles is marketing and advertisement. By utilizing IRB-approved marketing materials, we reach qualifying subjects via a variety of digital, print, and social media sources. Marketing and recruitment efforts are done centrally to reduce the burden on the individual sites, so they can focus on protocol execution and data management.",
  },
  {
    title: "One Company, One Solution",
    fact: "One department for every CTA",
    text: "One of the many benefits of working with KUR Research is the streamlined process we utilize for study start-up. Sponsors can expect to execute the CTA and budget for all KUR sites through one department, and all regulatory start up documents will be handled and maintained by a KUR regulatory representative. We understand that the start-up process is only the beginning of a long-term relationship with our sponsors, and KUR Research ensures an excellent experience from the start.",
  },
];

export const startup = {
  title: "Study start-up process",
  text: "Our Study Startup Process is based on the needs of the Sponsor. We are able to go from Site Selection to First Subject Enrolled in (as fast as) 10 days.",
  steps: [
    "SIV Scheduling",
    "Source Document Creation",
    "Source Document Validation",
    "Budget & Contract Negotiations",
    "Regulatory Submission",
  ],
};

export const centralized = {
  title: "Our centralized solutions",
  text: "Our Centralized Solutions are designed to promote quality and consistency across sites and protocols by centralizing relevant processes to our corporate office.",
  groups: [
    {
      title: "Start-up & regulatory",
      items: [
        "CDA Execution",
        "FQ Completion & Submission",
        "Site Identification Feasibility",
        "Clinical Protocol Evaluation",
        "Protocol Management Assignment",
        "Budget Negotiations",
        "Contract Negotiations",
        "Regulatory Activities",
        "IRB/IEC Submission",
      ],
    },
    {
      title: "Site operations & data",
      items: [
        "Informed Consent Process",
        "Staff Training",
        "Common SOP",
        "IT & CTMS Infrastructure",
        "Clinical Site Biz. Dev. & Marketing",
        "Quality Assurance / Quality Control",
        "Data Management / EDC Entry",
        "Query Resolution",
        "Subject Engagement & Recruitment",
      ],
    },
    {
      title: "Safety, supply & close-out",
      items: [
        "AE/SAE Identification and Processing",
        "Investigational Product & Supply Management",
        "Temperature Monitoring",
        "Monitoring and Audit Support",
        "Communication with CRO & Sponsors",
        "Study Close Out",
        "Patient Data and Site File Archiving",
        "Full AR/AP/Billing/Invoicing Cycle",
      ],
    },
  ],
};

export const specialtyPopulations = [
  { title: "Geriatrics" },
  { title: "Women's Health" },
  {
    title: "Pediatrics",
    text: "Pediatric clinical trials are essential to develop age-specific therapies and interventions to provide the best medical treatment available. KUR Research has sites experienced with the specific ethical and clinical concerns of conducting trials within this special population, from newborn through adolescents.",
  },
];

// Values transcribed from KUR's published demographic charts.
export const demographics = [
  {
    title: "Age distribution",
    rows: [
      { label: "18 and under", value: 20 },
      { label: "19 to 55", value: 56 },
      { label: "56 and over", value: 24 },
    ],
  },
  {
    title: "Sex distribution",
    rows: [
      { label: "Female", value: 55 },
      { label: "Male", value: 45 },
    ],
  },
  {
    title: "Ethnicity distribution",
    rows: [
      { label: "Hispanic or Latino", value: 67 },
      { label: "Non-Hispanic or Latino", value: 26 },
      { label: "Not specified", value: 7 },
    ],
  },
  {
    title: "Race distribution",
    rows: [
      { label: "Caucasian", value: 49 },
      { label: "Hispanic", value: 20 },
      { label: "African American", value: 10 },
      { label: "Unknown / Other", value: 10 },
      { label: "Asian", value: 7 },
      { label: "Two or more", value: 3 },
      { label: "Native American or Pacific Islander", value: 1 },
    ],
  },
];
